<template>
    <v-dialog
        :value="isOpen"
        max-width="500px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template v-if="!isNewProductionSite">
                        Edycja stanowiska raportowania <strong>{{ productionSite.name }}</strong>
                    </template>
                    <template v-else>
                        Tworzenie stanowiska raportowania
                    </template>
                </h1>
            </v-card-title>

            <v-card-text class="text-center">
                <v-text-field
                    v-model="productionSite.name"
                    label="Nazwa"
                    :error-messages="nameErrors"
                    required
                    outlined
                    dense
                />
                <v-autocomplete
                    v-model="productionSite.machineId"
                    label="Maszyna"
                    :items="machines"
                    :filter="customFilter"
                    item-value="id"
                    item-text="name"
                    no-data-text="Nie znaleziono maszyny"
                    :error-messages="machineIdErrors"
                    outlined
                    dense
                />
                <v-autocomplete
                    v-model="productionSite.printers"
                    :items="availablePrinters"
                    label="Dostępne drukarki"
                    item-text="name"
                    return-object
                    chips
                    clearable
                    multiple
                    outlined
                    dense
                    @change="setPrintersIds"
                >
                    <template #selection="data">
                        <v-chip
                            close
                            @update:active="removePrinter(data.item)"
                        >
                            {{ data.item.name }}
                        </v-chip>
                    </template>
                </v-autocomplete>
                <v-select
                    v-model="productionSite.printer"
                    :items="productionSite.printers"
                    label="Drukarka domyślna"
                    item-text="name"
                    return-object
                    required
                    no-data-text="Nie znaleziono drukarki"
                    :error-messages="defaultPrinterErrors"
                    outlined
                    dense
                    @change="setDefaultPrinterId"
                />
                <v-text-field
                    v-model="productionSite.number"
                    label="Numer"
                    :error-messages="numberErrors"
                    type="number"
                    required
                    outlined
                    dense
                />
                <v-text-field
                    v-model="productionSite.mpk"
                    label="Mpk"
                    outlined
                    dense
                />
                <v-select
                    v-model="productionSite.typeId"
                    label="Typ stanowiska"
                    :items="types"
                    item-text="name"
                    item-value="id"
                    required
                    outlined
                    dense
                    :error-messages="typeIdErrors"
                />
                <v-checkbox
                    v-model="productionSite.allowsSendingOrders"
                    label="Czy stanowisko zezwala na nadawanie zamówień?"
                />
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    :disabled="actionLoading"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>

                <v-spacer />

                <v-btn
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    :loading="actionLoading"
                    @click="saveProductionSite"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import customFilters from '@/mixins/customFilters'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { isEmpty } from 'lodash'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'ProductionSiteDialog',
    mixins: [customFilters],
    props: {
        isOpen: {
            type: Boolean
        },
        productionSiteData: {
            type: Object,
            default: null
        },
        availablePrinters: {
            type: Array,
            default: () => []
        },
        machines: {
            type: Array,
            default: () => []
        },
        types: {
            type: Array,
            default: () => []
        },
        allowedReportItemTypes: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            productionSite: {
                name: null,
                machineId: null,
                printersIds: [],
                printers: [],
                defaultPrinterId: null,
                printer: {},
                number: null,
                typeId: null,
            },
            actionLoading: false
        }
    },
    validations() {
        return {
            productionSite: {
                name: {
                    required
                },
                machineId: {
                    required
                },
                typeId: {
                    required
                },
                number: {
                    required
                },
                printer: {
                    required: requiredIf(() => this.productionSite.printers.length > 0)
                }
            }
        }
    },
    computed: {
        isNewProductionSite() {
            return isEmpty(this.productionSiteData)
        },
        // Walidacje
        nameErrors() {
            const errors = []

            if (!this.$v.productionSite.name.$dirty) return errors
            !this.$v.productionSite.name.required && errors.push('Proszę podać nazwę procesu produkcyjnego.')

            return errors
        },
        machineIdErrors() {
            const errors = []

            if (!this.$v.productionSite.machineId.$dirty) return errors
            !this.$v.productionSite.machineId.required && errors.push('Proszę wybrać maszynę.')

            return errors
        },
        typeIdErrors() {
            const errors = []

            if (!this.$v.productionSite.typeId.$dirty) return errors
            !this.$v.productionSite.typeId.required && errors.push('Proszę wybrać typ stanowiska.')

            return errors
        },
        numberErrors() {
            const errors = []

            if (!this.$v.productionSite.number.$dirty) return errors
            !this.$v.productionSite.number.required && errors.push('Proszę podać numer.')

            return errors
        },
        defaultPrinterErrors() {
            const errors = []

            if (!this.$v.productionSite.printer.$dirty) return errors
            !this.$v.productionSite.printer.required && errors.push('Proszę wybrać domyślną drukarkę.')

            return errors
        }
    },
    watch: {
        isOpen(value) {
            if (value) {
                this.productionSite = JSON.parse(JSON.stringify(this.productionSiteData))
                this.setDefaultPrinterId()
            }
        }
    },
    methods: {
        setDefaultPrinterId() {
            this.productionSite.defaultPrinterId = this.productionSite.printer ? this.productionSite.printer.id : null
        },
        setPrintersIds() {
            this.productionSite.printersIds = this.productionSite.printers.map(obj => obj.id)
        },
        removePrinter(item) {
            const index = this.productionSite.printersIds.indexOf(item.id)

            if (index > -1) {
                this.productionSite.printers = this.productionSite.printers.filter(printer => printer.id !== item.id)
                this.productionSite.printersIds = this.productionSite.printers.map(obj => obj.id)
            }

            if (this.productionSite.printer && item.id === this.productionSite.printer.id) {
                this.productionSite.printer = null
            }
        },
        saveProductionSite() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })
            } else {
                let action = null

                if (this.isNewProductionSite) {
                    action = 'createProductionSite'
                } else {
                    action = 'updateProductionSite'
                }

                this.actionLoading = true
                this.$store.dispatch(`configurations/${action}`, this.productionSite)
                    .then(() => {
                        this.$emit('on-save')
                        this.$v.$reset()
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.actionLoading = false
                        this.onClose()
                    })
            }
        },
        onClose() {
            setTimeout(() => {
                this.productionSite = {
                    name: null
                }
                this.$v.$reset()
            }, 500)

            this.$emit('on-close')
        }
    }
}
</script>